import {
  Confirm,
  DateField,
  Datagrid,
  FunctionField,
  List,
  NumberField,
  SelectField,
  ShowButton,
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  useDataProvider,
  useNotify,
  useRefresh,
  useRecordContext,
  WrapperField,
} from "react-admin";
import React, { useState, useEffect, useCallback } from "react";
import Launch from "@mui/icons-material/Launch";
import Cancel from "@mui/icons-material/Cancel";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import formatJson from "format-json";
import { CommentButton } from "./button/CommentButton";
import Rating from "@mui/lab/Rating";
import {
  FLOOR_PLAN_URL_TEMPLATE,
  LAYOUT_URL_TEMPLATE,
  PLAYER_URL_TEMPLATE,
} from "./config";
import { IconButton, Tooltip } from "@mui/material";
import { CopyAllOutlined } from "@mui/icons-material";

const STATUSES = [
  { id: "model_grow", label: "формирование модели" },
  { id: "grower_error", label: "ошибка формирования модели" },
  { id: "model_upload ", label: "загрузка модели" },
  { id: "render_queued", label: "рендер" },
  { id: "render_upload", label: "загрузка рендера" },
  { id: "render_error", label: "ошибка рендера" },
  { id: "postprocess", label: "постобработка" },
  { id: "screenshot_queued", label: "формируется скриншот" },
  { id: "screenshot_error", label: "ошибка скриншота" },
  { id: "postprocess_upload", label: "ошибка постобработки" },
  { id: "publisher_error", label: "ошибка постобработки" },
  { id: "done", label: "готов" },
  { id: "error", label: "ошибка" },
  { id: "canceled", label: "отменен" },
];

// A render task can be canceled only while it is still in progress — i.e. not in
// one of the terminal statuses (mirrors RenderTask::isComplete on the backend).
const NON_CANCELABLE_STATUSES = [
  "done",
  "grower_error",
  "render_error",
  "publisher_error",
  "error",
  "canceled",
];

const FloorPlanLink = (id) =>
  id ? <a href={FLOOR_PLAN_URL_TEMPLATE.replace("%id", id)}>{id}</a> : "-";

const LayoutLink = (floorPlanId, layoutId) =>
  layoutId ? (
    <a
      href={LAYOUT_URL_TEMPLATE.replace("%floor_plan_id", floorPlanId).replace(
        "%layout_id",
        layoutId
      )}
    >
      {layoutId}
    </a>
  ) : (
    "-"
  );

const RenderRenderUrl = (record) =>
  record.status === "done" ? (
    <a href={PLAYER_URL_TEMPLATE.replace("%id", record.id)}>
      <Launch />
    </a>
  ) : null;

const RenderCommentButton = () => {
  const { comment, id } = useRecordContext();
  const dataProvider = useDataProvider();
  const [values, setValues] = useState(null);
  const notify = useNotify();
  const refresh = useRefresh();

  useEffect(() => {
    if (values) {
      const { comment } = values;
      dataProvider
        .update("renders", { id, data: { comment } })
        .then(() => {
          notify("Комментарий сохранен");
          refresh();
        })
        .catch((e) => notify("Ошибка сохранения комментария: " + e, "error"));
    }
  }, [id, dataProvider, notify, refresh, values]);

  return (
    <CommentButton
      initialValues={{ comment }}
      onSubmit={(values) => setValues(values)}
    />
  );
};

const CancelRenderButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);

  if (!record || NON_CANCELABLE_STATUSES.includes(record.status)) {
    return null;
  }

  const handleCancel = () => {
    setOpen(false);
    dataProvider
      .cancel("renders", { id: record.id })
      .then(() => {
        notify("Рендер отменён");
        refresh();
      })
      .catch((e) => notify("Ошибка отмены рендера: " + e, "error"));
  };

  return (
    <>
      <Tooltip title="Отменить">
        <IconButton onClick={() => setOpen(true)} size="small" color="error">
          <Cancel fontSize="small" />
        </IconButton>
      </Tooltip>
      {open && (
        <Confirm
          isOpen={open}
          title="Отмена рендера"
          content="Отменить рендер? Статус сбросится, и для планировки можно будет запустить новый рендер."
          onConfirm={handleCancel}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const renderParams = ({ id, comment, bakeSamples, bakeTypes, bakeSizes }) => (
  <React.Fragment>
    {[bakeSizes, bakeTypes, bakeSamples, comment]
      .filter((v) => (Array.isArray(v) ? v.length !== 0 : v))
      .map((v) => (Array.isArray(v) ? v.join(" / ") : v))
      .map((v, k) => (
        <div key={k}>{v}</div>
      ))}
  </React.Fragment>
);

const financial = (num) => (num ? Math.round(parseFloat(num) * 10) / 10 : "-");

const totalCost = ({ priceGPU, priceCPU, postprocessCost }) => (
  <span
    title={`${financial(priceGPU)} / ${financial(priceCPU)} / ${financial(
      postprocessCost
    )}`}
  >
    {financial(
      [priceGPU, priceCPU, postprocessCost].reduce(
        (a, b) => a + parseFloat(b ?? 0),
        0
      )
    )}
  </span>
);

const totalTime = ({ renderTimeGPU, renderTimeCPU, postprocessTime }) => (
  <span
    title={`${financial(renderTimeGPU / 60)} / ${financial(
      renderTimeCPU / 60
    )} / ${financial(postprocessTime / 60)}`}
  >
    {financial(
      [renderTimeGPU, renderTimeCPU, postprocessTime].reduce(
        (a, b) => a + (b > 0 ? b : 0),
        0
      ) / 60
    )}
  </span>
);

const RenderQualityButton = () => {
  const { status, id, quality } = useRecordContext();

  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();

  const setValue = useCallback(
    (event, value) => {
      dataProvider
        .update("renders", { id, data: { quality: value } })
        .then(() => {
          notify("Оценка сохранена");
          refresh();
        })
        .catch((e) => notify("Ошибка сохранения: " + e, "error"));
    },
    [dataProvider, id, notify, refresh]
  );

  return (
    <Rating
      name={"quality" + id}
      value={quality ? quality : 0}
      readOnly={status !== "done"}
      onChange={setValue}
    />
  );
};

const CopyButton = ({ text }) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      console.log("Скопировано:", text);
    } catch (err) {
      console.error("Ошибка копирования:", err);
    }
  };

  return (
    <Tooltip title="Скопировать">
      <IconButton onClick={handleCopy}>
        <CopyAllOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const IdWithCopyButton = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span>{record.id.substring(0, 6)}</span>
      <CopyButton text={record.id.toString()} />
    </div>
  );
};

export const RenderDataGrid = (props) => (
  <Datagrid {...props}>
    <WrapperField label="id">
      <IdWithCopyButton />
      <TextField source="comment" />
    </WrapperField>
    <WrapperField label="Статус">
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        <SelectField source="status" choices={STATUSES} optionText="label" />
        <CancelRenderButton />
      </span>
    </WrapperField>
    <DateField source="createTime" showTime label="Создан" />
    <DateField source="updateTime" showTime label="Обновлен" />
    <DateField source="finishTime" showTime label="Завершен" />
    <FunctionField render={totalCost} label="Стоимость" />
    <FunctionField render={totalTime} label="Compute time" />
    <RenderQualityButton />
    <FunctionField render={RenderRenderUrl} label="" />
    <ShowButton label="" />
    <RenderCommentButton />
    {props.children}
  </Datagrid>
);

export const RenderList = (props) => (
  <List {...props} sort={{ field: "createTime", order: "DESC" }}>
    <RenderDataGrid />
  </List>
);

const JsonField = ({ source }) => {
  const record = useRecordContext();
  return (
    <SyntaxHighlighter language="javascript" style={docco}>
      {formatJson.plain(record[source] || null)}
    </SyntaxHighlighter>
  );
};

const RenderId = (source, record = {}) => {
  const v = record[source];
  return v ? `${v}` : "-";
};

const RenderFullId = (source, record = {}) => {
  const v = record[source];
  return v ? `${v} (0x${v.replace(/-/g, "")})` : "-";
};

export const RenderShow = (props) => (
  <Show {...props}>
    <TabbedShowLayout>
      <Tab label="General">
        <FunctionField
          label="Id"
          render={(record) => RenderFullId("id", record)}
        />
        <FunctionField render={renderParams} source="id" label="Параметры" />
        <TextField source="targetType" label="Цель" />
        <FunctionField
          label="Планировка"
          render={(record) => LayoutLink(record.layoutFloorPlan, record.layout)}
        />
        <FunctionField
          label="GrowerTask"
          render={(record) => RenderId("growerTask", record)}
        />
        <SelectField
          source="status"
          choices={STATUSES}
          optionText="label"
          label="Статус"
        />
        <TextField source="customRenderS3Src" label="Custom render source" />
        <DateField source="createTime" showTime label="Создан" />
        <DateField source="updateTime" showTime label="Обновлен" />
        <DateField source="finishTime" showTime label="Завершен" />
        <NumberField source="priceGPU" label="GPU" />
        <NumberField source="renderTimeGPU" label="Время GPU" />
        <NumberField source="priceCPU" label="CPU" />
        <NumberField source="renderTimeCPU" label="Время CPU" />
      </Tab>
      <Tab label="Jobs">
        <JsonField source="renderJobs" />
      </Tab>
      <Tab label="Time explained">
        <JsonField source="renderTimePerJob" />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
